/**
 * Export invoices to CSV with clean, Excel-compatible formatting
 * Following Apple's "it just works" philosophy
 */

export interface Invoice {
  invoiceNumber: string;
  vendor: string;
  buyer?: string;
  amount: number;
  /** ISO code, or '' when genuinely unknown. Never a symbol, never invented. */
  currency?: string;
  date: Date | string;
  dueDate?: Date | string;
  /** Already-human workflow status, matching what the table displays. */
  status: string;
  validationScore?: number;
}

/**
 * Export invoices to CSV with proper formatting
 */
export function exportInvoicesToCSV(invoices: Invoice[], filename?: string): void {
  // Headers with business-friendly names
  const headers = [
    'Invoice Number',
    'Seller',
    'Buyer',
    'Amount',
    'Currency',
    'Invoice Date',
    'Due Date',
    'Days Until Due',
    'Status',
    'Extraction Confidence'
  ];

  // Format data with proper escaping
  const rows = invoices.map(inv => [
    inv.invoiceNumber,
    inv.vendor,
    inv.buyer || '',
    inv.amount.toFixed(2),
    // Blank when unknown. This used to default to 'USD', so every amount whose
    // currency could not be detected was exported as US dollars.
    normaliseCurrency(inv.currency),
    formatDateForCSV(inv.date),
    formatDateForCSV(inv.dueDate || ''),
    calculateDaysUntilDue(inv.dueDate),
    inv.status || '',
    inv.validationScore !== undefined ? `${inv.validationScore}%` : ''
  ]);

  // Create CSV with proper escaping
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(escapeCSV).join(','))
  ].join('\n');

  // Trigger download with timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const finalFilename = filename || `invoices-${timestamp}.csv`;

  downloadCSV(csvContent, finalFilename);
}

/**
 * Escape CSV fields properly
 */
function escapeCSV(field: string | number): string {
  const str = String(field);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Format date as YYYY-MM-DD (Excel-friendly)
 */
function formatDateForCSV(date: Date | string): string {
  if (!date) return 'N/A';

  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return 'N/A';
    return d.toISOString().split('T')[0];
  } catch {
    return 'N/A';
  }
}

/**
 * Calculate days until due (negative if overdue)
 */
function calculateDaysUntilDue(dueDate?: Date | string): string {
  if (!dueDate) return 'N/A';

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
    due.setHours(0, 0, 0, 0);

    const diffTime = due.getTime() - today.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (days < 0) return `${days} (Overdue)`;
    if (days === 0) return '0 (Today)';
    return String(days);
  } catch {
    return 'N/A';
  }
}

/**
 * Emit an ISO 4217 code, never a symbol and never a guess.
 *
 * Uploaded documents carry a detected symbol ('€') while created invoices carry
 * an ISO code ('PLN'), so the column used to mix the two. Anything unrecognised
 * exports blank rather than being assigned a currency it may not be in.
 */
const SYMBOL_TO_ISO: Record<string, string> = {
  '$': 'USD', '€': 'EUR', '£': 'GBP', '¥': 'JPY',
  'zł': 'PLN', 'lei': 'RON',
};

function normaliseCurrency(currency?: string): string {
  const value = currency?.trim();
  if (!value) return '';
  if (/^[A-Za-z]{3}$/.test(value)) return value.toUpperCase();
  return SYMBOL_TO_ISO[value] || '';
}

/**
 * Trigger browser download
 */
function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
